import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useQuery,
} from "@tanstack/react-query";
import * as userService from "@/features/users/userService";

/**
 * Hook for creating a new user (Admin/Manager only)
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  const createUser = useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  return {
    createUser: createUser.mutate,
    createUserAsync: createUser.mutateAsync,
    isPending: createUser.isPending,
    isError: createUser.isError,
    error: createUser.error,
  };
};

export const useUsersInfiniteQuery = (params) => {
  const searchParams = new URLSearchParams(params);
  return useInfiniteQuery({
    queryKey: ["users", params],
    queryFn: async ({ pageParam }) => {
      return await userService.fetchAllUsers(
        `page=${pageParam}&${searchParams.toString()}`
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.nextPage !== null
        ? lastPage.pageInfo.nextPage
        : undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2, // Retry failed requests up to 2 times
  });
};

export const useUpdateActiveStatus = () => {
  const queryClient = useQueryClient();

  const updateActiveStatus = useMutation({
    mutationFn: userService.updateActiveStatus,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      // Also invalidate the specific user query if we're viewing a user profile
      queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
    },
  });
  return {
    updateActiveStatus: updateActiveStatus.mutate,
    isPending: updateActiveStatus.isPending,
  };
};

export const useUserQuery = (id) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: () => userService.fetchUserById(id),
  });
};

export const useUsersQuery = (params) => {
  const searchParams = new URLSearchParams(params);
  return useQuery({
    queryKey: ["users", params],
    queryFn: () => userService.fetchAllUsers(searchParams),
  });
};

// New hook specifically for search functionality with debouncing
export const useUsersSearchQuery = (
  search = "",
  limit = 50,
  page = 1,
  enabled = true,
  query = { role: "client" } // Default role filter
) => {
  const params = new URLSearchParams({
    brandName_q: search,
    limit: limit.toString(),
    page: page.toString(),
    ...query, // Include any additional query parameters
  });

  return useQuery({
    queryKey: ["users-search", search, limit, page, query],
    queryFn: () => userService.fetchAllUsers(params.toString()),
    enabled: enabled && search.length >= 0, // Enable search even for empty strings to show all users
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  const updateUser = useMutation({
    mutationFn: userService.updateUser,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      // Invalidate the specific user query if we have the user ID
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
      }
      // Also invalidate all user queries to catch any cached user data
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
  const updateUserBank = useMutation({
    mutationFn: userService.updateUserBank,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      // Invalidate specific user if ID is available
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
      }
    },
  });
  const updateProfileImage = useMutation({
    mutationFn: userService.userUpdateProfileImage,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      // Invalidate specific user if ID is available
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
      }
    },
  });
  return {
    updateProfileImage: updateProfileImage.mutate,
    updateProfileImageLoading: updateProfileImage.isLoading,
    updateUserBank: updateUserBank.mutate,
    updateUserBankLoading: updateUserBank.isLoading,
    updateUser: updateUser.mutate,
    isLoading: updateUser.isPending,
  };
};

export const useChangeUserPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userService.changeUserPassword,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  const updateUserRole = useMutation({
    mutationFn: userService.updateUserRole,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
    },
  });

  return {
    updateUserRole: updateUserRole.mutate,
    isPending: updateUserRole.isPending,
  };
};

// Banking validation hooks
export const useBankingValidation = () => {
  const queryClient = useQueryClient();

  const approveBanking = useMutation({
    mutationFn: userService.approveBankingInfo,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["pending-banking"] });
    },
  });

  const rejectBanking = useMutation({
    mutationFn: userService.rejectBankingInfo,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["pending-banking"] });
    },
  });

  return {
    approveBanking: approveBanking.mutate,
    approveBankingLoading: approveBanking.isPending,
    rejectBanking: rejectBanking.mutate,
    rejectBankingLoading: rejectBanking.isPending,
  };
};

export const usePendingBankingRequests = (params = "") => {
  return useQuery({
    queryKey: ["pending-banking", params],
    queryFn: () => userService.getPendingBankingRequests(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
